/**
 * `/metal-resources/tutorials/`: the ryerson.com video tutorials, Ryerson build only.
 *
 * At build time `getStaticProps` fetches every Contentful `tutorial` in all three languages with
 * `src/contentful/tutorials/getContefulTutorial.ts` (a failure fails the build). The page shows the
 * current language's first five as the featured block and the rest below the newsletter sign-up,
 * through `src/components/Tutorials/VideoList/VideoList.tsx`. The order is the fetcher's (see its
 * header), so which five are featured is set there, not here.
 *
 * Hero and SEO copy: `src/content/ryerson/metal-resources/tutorials/index.ts`, which is also the
 * page's site-search entry.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import HeroComponent from "@components/Tutorials/Hero/Hero";
import { HeroContent } from "@content/ryerson/metal-resources/tutorials";
import VideoListComponent from "@components/Tutorials/VideoList/VideoList";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/metal-resources/tutorials";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import getContentfulTutorials from "@contentful/tutorials/getContefulTutorial";
import { ProcessedContentfulTutorialsFields } from "@contentful/tutorials/types";

/**
 * Build-time props: `notFound` (a 404) on any build but Ryerson; otherwise the header and footer
 * data plus every tutorial, keyed by language.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();
	const tutorialsByLocale = await getContentfulTutorials();

	return {
		props: {
			...headerFooterProps,
			tutorialsByLocale,
		},
	};
};

/** Renders the tutorials in the visitor's language: the first five featured, the rest below. */
const Page: React.FC<
	HeaderPageProps & { tutorialsByLocale: Record<string, ProcessedContentfulTutorialsFields[]> }
> = (props) => {
	const { tutorialsByLocale } = props;
	const {
		localization: { language },
	} = useApplication();

	const tutorials = tutorialsByLocale[language];
	const topVideos = tutorials.slice(0, 5);
	const bottomVideos = tutorials.slice(5);

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<HeroComponent content={HeroContent[language]} />
			<VideoListComponent topVideos={topVideos} bottomVideos={bottomVideos} />
		</HeaderLayout>
	);
};

export default Page;
